/** The words around the feedback page's viewer, chips and pins (#648). The verdict wording itself stays in `homework-feedback-labels`. */

import { MARK_SYMBOLS } from '../../i18n/homework-feedback-labels.js';
import { defineMessages } from '../../i18n/messages.js';
import type { FeedbackNote } from './feedback-model.js';

export const feedbackChrome = defineMessages({
  en: {
    summaryList: 'Summary',
    chipChecked: '✓ Checked',
    chipLook: '✗ Refuted',
    chipReview: '◌ Under review',
    chipNotCheckable: '○ Not checked',
    chipPointer: '⚑ Look here',
    viewer: 'Your pages',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    fit: 'Fit',
    pageOf: (n: number, total: number): string => `Page ${String(n)} · ${String(total)} pages`,
    pagesNav: 'Pages',
    thumb: (n: number, current: boolean): string => (current ? `Page ${String(n)}, current` : `Page ${String(n)}`),
    pin: (n: number, name: string, flagged: boolean): string => `Note ${String(n)}: ${name}${flagged ? ', flagged to check' : ''}`,
    postFixed: 'Post a fixed version',
    allFindings: 'All findings',
    stageLabel: (n: number): string => `Page ${String(n)} of your homework, drag to move and zoom`,
    hintDesktop: 'Drag to move · scroll to zoom',
    hintTouch: 'Drag to move · pinch to zoom',
    readNotChecked: 'Lines read, not checked',
    readAs: 'Read as:',
    mostReadNotChecked: 'Most of this page was read and not checked.',
    noNotesOnPage: 'No notes on this page.',
    alsoToLook: 'Also to look at:',
    ourGuess: 'our guess',
    sureQuestion: 'Question',
    sureWhole: 'The whole homework',
    sureHeading: 'How sure were you?',
    sureSkip: 'Skip this one',
    sureAllAnswered: 'All answered',
    sureShow: 'Show the feedback',
    notCheckedYetHeading: 'Not checked yet',
    secondReaderCouldNotRun: 'The second reader could not run',
    needsMoodle: 'It needs the question sheets from Moodle.',
    connectMoodle: 'Connect Moodle',
    withoutSheetHead: 'Read without the question sheet',
    withoutSheetLine: 'DaVinci saw your work but not the questions.',
    // The board's on-page notes (#661).
    questionStrip: (label: string): string => `Question ${label}`,
    tagNotChecked: 'Read, not checked',
    uncheckedSummary: 'These lines were read and not checked. Dashed grey frames mark them.',
    pointerNotVerdict: 'A pointer, not a verdict',
    courseSource: 'From the course:',
    seeWholePage: 'See the whole page',
    readBlockByBlock: 'Read block by block',
    closeNote: 'Close',
    enlargeCrop: 'Enlarge your writing',
    noteSheetLabel: (title: string): string => `Note: ${title}`,
    wholePageLabel: (n: number): string => `Page ${String(n)}, drag to move and zoom`,
    blocksList: 'The notes, in reading order',
    noteGutter: 'Notes on this page',
    countsAsHelp: 'Seeing this feedback counts as help, and every showing is recorded.',
  },
  he: {
    summaryList: 'סיכום',
    chipChecked: '✓ נבדקו',
    chipLook: '✗ נסתר',
    chipReview: '◌ בבדיקה',
    chipNotCheckable: '○ לא נבדק',
    chipPointer: '⚑ לשים לב',
    viewer: 'העמודים שלכם',
    previousPage: 'העמוד הקודם',
    nextPage: 'העמוד הבא',
    zoomOut: 'הקטנה',
    zoomIn: 'הגדלה',
    fit: 'התאמה',
    pageOf: (n: number, total: number): string => `עמוד ${String(n)} · ${String(total)} עמודים`,
    pagesNav: 'עמודים',
    thumb: (n: number, current: boolean): string => (current ? `עמוד ${String(n)}, הנוכחי` : `עמוד ${String(n)}`),
    pin: (n: number, name: string, flagged: boolean): string => `הערה ${String(n)}: ${name}${flagged ? ', מסומנת לבדיקה' : ''}`,
    postFixed: 'לשלוח גרסה מתוקנת',
    allFindings: 'כל הממצאים',
    stageLabel: (n: number): string => `עמוד ${String(n)} מתוך שיעורי הבית, אפשר להזיז ולהתקרב`,
    hintDesktop: 'לגרור כדי להזיז · לגלול כדי להתקרב',
    hintTouch: 'לגרור כדי להזיז · לצבוט כדי להתקרב',
    readNotChecked: 'שורות שנקראו ולא נבדקו',
    readAs: 'נקרא כך:',
    mostReadNotChecked: 'רוב העמוד נקרא ולא נבדק.',
    noNotesOnPage: 'אין הערות בעמוד הזה.',
    alsoToLook: 'עוד לבדוק:',
    ourGuess: 'הערכה שלנו',
    sureQuestion: 'שאלה',
    sureWhole: 'כל שיעורי הבית',
    sureHeading: 'מה רמת הביטחון שלך בתשובה?',
    sureSkip: 'לדלג על השאלה הזו',
    sureAllAnswered: 'כל השאלות נענו',
    sureShow: 'להציג את המשוב',
    notCheckedYetHeading: 'עדיין לא נבדק',
    secondReaderCouldNotRun: 'הקורא השני לא הופעל',
    needsMoodle: 'דרושים דפי השאלות מ-Moodle.',
    connectMoodle: 'לחבר את Moodle',
    withoutSheetHead: 'נקרא בלי דף השאלות',
    withoutSheetLine: 'הבדיקה ראתה את העבודה, בלי השאלות.',
    questionStrip: (label: string): string => `שאלה ${label}`,
    tagNotChecked: 'נקרא, לא נבדק',
    uncheckedSummary: 'השורות האלה נקראו ולא נבדקו. מסגרות אפורות מקווקוות מסמנות אותן.',
    pointerNotVerdict: 'הצבעה, לא פסק',
    courseSource: 'מהקורס:',
    seeWholePage: 'לראות את כל העמוד',
    readBlockByBlock: 'לקרוא בלוק אחרי בלוק',
    closeNote: 'סגירה',
    enlargeCrop: 'הגדלת הכתב',
    noteSheetLabel: (title: string): string => `הערה: ${title}`,
    wholePageLabel: (n: number): string => `עמוד ${String(n)}, אפשר להזיז ולהתקרב`,
    blocksList: 'ההערות, לפי סדר הקריאה',
    noteGutter: 'הערות על העמוד',
    countsAsHelp: 'הצגת המשוב נחשבת לעזרה, וכל הצגה נרשמת.',
  },
});


/** The symbol on a pin or a marker: the shared one, except that a line under review is the board's ◌ (the symbol is decoration; the word is read). */
export function markGlyph(mark: FeedbackNote['mark']): string {
  return mark === 'under_review' ? '◌' : MARK_SYMBOLS[mark];
}

/** Which of the four chip families a mark counts in, or none. */
export function markFamily(mark: FeedbackNote['mark']): 'checked' | 'look' | 'review' | 'not_checkable' | null {
  switch (mark) {
    case 'proved':
    case 'ruled_correct':
      return 'checked';
    case 'refuted':
      return 'look';
    case 'under_review':
      return 'review';
    case 'read_not_checked':
    case 'refused':
    case 'undecided':
    case 'withheld':
      return 'not_checkable';
    default:
      return null;
  }
}
